import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  InputLabel,
  Grid,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Logo from "../Image/Logo.png";
import CustomSnackbar from "../Utils/CustomSnackbar";
import { emailLink } from "../api/action";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { sanitizeInput, validateEmailText } from "../Utils/validationSecurity";

const ForgetPassword = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ✅ Email Validation
  const validateEmail = (value) => {
    const error = validateEmailText(value, "Email");
    setEmailError(error || "");
    return !error;
  };

  // ✅ Send Reset Link
  const handleSendEmail = (e) => {
    e.preventDefault();

    if (!email || !validateEmail(email)) {
      setSnackbarMessage("Please enter a valid email.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    dispatch(
      emailLink(
        { email: sanitizeInput(email) },
        (res) => {
          setLoading(false);
          if (
            res?.data?.msg ===
            "Password Reset link send. Please check your Email"
          ) {
            setSnackbarMessage(res?.data?.msg);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setEmail("");
            setTimeout(() => navigate("/"), 2000);
          } else {
            setSnackbarMessage(res?.data?.msg);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        },
        (error) => {
          setLoading(false);
          const msg =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.detail || "Oops! Please try again.";
          setSnackbarMessage(msg);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  return (
    <Grid container sx={{ height: "100vh" }}>
      {/* Left Side - Branding */}
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          position: "relative",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(to bottom right, rgba(29, 57, 105, 1), rgba(0, 0, 0, 1))",
            zIndex: 1,
          }}
        />

        {/* Branding Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            color: "#fff",
            maxWidth: 400,
          }}
        >
          <img
            src={Logo}
            alt="Healthcare Logo"
            style={{
              width: "100%",
              height: "100%",
              background: "#fff",
              padding: "8px",
              borderRadius: "12px",
            }}
          />
        </Box>
      </Grid>

      {/* Right Side - Form */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f9fafb",
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: "16px" }}
        >
          <Box textAlign="center" mb={3}>
            <MailOutlineIcon sx={{ fontSize: 50, color: "rgba(29, 57, 105, 1)" }} />
            <Typography variant="h6" fontWeight="bold">
              Forgot Password?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We’ll send a reset link to your email
            </Typography>
          </Box>

          {/* Email Input */}
          <InputLabel>Email Address</InputLabel>
          <TextField
            fullWidth
            margin="dense"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              validateEmail(e.target.value);
            }}
            error={!!emailError}
            helperText={emailError}
            placeholder="Enter your email"
          />

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              mt: 3,
              py: 1.5,
              background:
             "rgba(29, 57, 105, 1)",
              fontWeight: "bold",
              borderRadius: "8px",
              color: "#ffffff",
            }}
            onClick={handleSendEmail}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Send Reset Link"
            )}
          </Button>

          <Divider sx={{ my: 3 }}>or</Divider>

          {/* Back to login */}
          <Typography
            textAlign="center"
            sx={{
              mt: 2,
              color: "rgba(29, 57, 105, 1)",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: "500",
              textDecoration: "underline",
            }}
            onClick={() => navigate("/")}
          >
            ⬅ Back to Login
          </Typography>
        </Paper>
      </Grid>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Grid>
  );
};

export default ForgetPassword;
